<script lang="ts">
	import {
		DataHandlerFolderAdmin,
		DataHandlerMediaAdmin,
		Media,
	} from "luxedo-data"
	import { Folder } from "luxedo-data/src/entries/Folder"
	import { FileSystemMedia } from "../../../modules/FileSystems"

	import { type ContextMenuOptions, initContextMenu } from "context-menu"
	import { openTextOverlay } from "overlay"

	import { FolderIcon, TrashIcon, EditIcon } from "luxedo-icons"
	import {
		clearMediaDrag,
		MediaDragStore,
		setMediaDrag,
	} from "../../../stores/MediaDragStore"

	interface Props {
		// File / Folder to render
		file: Folder | Media
		// Called on double-click
		onSelect?: () => void
	}

	let { file, onSelect = () => {} }: Props = $props()

	let isDropHover = $state(false)
	let mediaDragID: number

	MediaDragStore.subscribe((data) => (mediaDragID = data?.id ?? -1))

	const ctxMenuDefaults: ContextMenuOptions = [
		{
			title: "Rename",
			icon: EditIcon,
			onClick: async () => {
				openTextOverlay(
					{
						defaultValue: file.name,
						label: "New Name",
						onSubmit: async (newName: string) => {
							await FileSystemMedia.rename(file, newName)
						},
					},
					{
						heading: `Rename ${file instanceof Folder ? "folder" : "file"}`,
					}
				)
			},
		},
		{
			title: "Delete",
			icon: TrashIcon,
			onClick: async () => {
				await FileSystemMedia.delete(file)
			},
		},
	]

	function initDrag(e: DragEvent) {
		e.dataTransfer.dropEffect = "move"
		e.dataTransfer.setData("type", file instanceof Folder ? "folder" : "file")
		e.dataTransfer.setData("file", String(file.id))
		setMediaDrag(file.id)
	}

	function onDrop(e: DragEvent) {
		e.preventDefault()
		isDropHover = false

		const dataType = e.dataTransfer.getData("type")
		const dataID = Number(e.dataTransfer.getData("file"))

		if (!(file instanceof Folder))
			throw "Can only drop files/folders into folders."
		if (dataType === "file") {
			const dropFile = DataHandlerMediaAdmin.get(dataID)
			FileSystemMedia.move(dropFile, file)
		} else {
			const dropFolder = DataHandlerFolderAdmin.get(dataID)
			FileSystemMedia.move(dropFolder, file)
		}

		clearMediaDrag()
	}

	function initDropHover(e: DragEvent) {
		e.preventDefault()

		if (file instanceof Media || mediaDragID === file.id) return
		isDropHover = true
	}

	function endDropHover(e: DragEvent) {
		isDropHover = false
	}
</script>

<div
	class="Tile {isDropHover ? 'drop' : ''}"
	tabindex="0"
	role="button"
	title={file.name}
	ondragstart={initDrag}
	ondragover={file instanceof Folder ? initDropHover : null}
	ondragleave={endDropHover}
	ondragend={endDropHover}
	ondrop={onDrop}
	oncontextmenu={initContextMenu(ctxMenuDefaults)}
	ondblclick={onSelect}
	onkeypress={(e) => {
		if (e.key === "Enter") onSelect()
	}}
	draggable={true}
>
	{#if file instanceof Folder}
		<FolderIcon />
	{:else}
		<img src={file.src.thumbnail} alt={`${file.name} Thumbnail`} />
	{/if}
	<span class="media-label">
		<span class="media-name">
			{file.name}
		</span>
	</span>
</div>

<style>
	.Tile {
		user-select: none;
		cursor: pointer;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: space-between;
		width: 10%;
		height: 8rem;
		border-radius: var(--br);
		margin: 0.25rem;
		min-width: 8rem;
		background-color: var(--color-accent);
		color: var(--color-text-light);
		padding: 0.5rem;
		box-shadow: var(--shadow-medium);
		transition:
			background-color var(--transition-duration),
			color var(--transition-duration),
			box-shadow var(--transition-duration),
			transform var(--transition-duration);
	}

	.Tile.drop {
		transform: scale(1.2);
	}

	.media-label {
		text-align: center;
		width: 100%;
		font-size: var(--text-small);
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	img {
		max-height: calc(100% - 2rem);
		border-radius: var(--br);
	}

	.Tile:hover,
	.Tile:focus-visible {
		background-color: var(--color-main);
		color: var(--color-text);
		box-shadow: var(--shadow-large);
	}

	.Tile :global(.svg-stroke) {
		stroke: var(--color-text-light);
	}

	.Tile:hover :global(.svg-stroke),
	.Tile:focus-visible :global(.svg-stroke) {
		stroke: var(--color-text);
	}
</style>
